import { useState } from 'react';
import { CheckCircle2, Clock } from 'lucide-react';
import { useCheckInStatus } from '../api/checkIns';
import { useEngineerList } from '../api/engineers';
import PageHeader from '../components/layout/PageHeader';
import Badge from '../components/ui/Badge';
import { TablePageSkeleton } from '@/components/ui/skeleton';
import ErrorState from '../components/ui/ErrorState';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const INPUT_CLS = 'h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring';

export default function CheckInStatusPage() {
  const todayIso = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayIso);

  const { data: status, isLoading: loadingStatus, error, refetch } = useCheckInStatus(date);
  const { data: engineers, isLoading: loadingEng } = useEngineerList();

  const isLoading = loadingStatus || loadingEng;

  if (isLoading) return <TablePageSkeleton cols={3} hasAction={false} />;
  if (error)     return <ErrorState onRetry={refetch} />;

  const activeEngineers = engineers?.filter((e) => e.isActive) ?? [];
  const missingSet      = new Set(status?.missingEngineers ?? []);
  const checkedIn       = activeEngineers.filter((e) => !missingSet.has(e.id));
  const notCheckedIn    = activeEngineers.filter((e) =>  missingSet.has(e.id));
  const displayDate     = new Date(date + 'T00:00:00').toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className="max-w-lg">
      <PageHeader title="Check-in status" description={displayDate} />

      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm text-muted-foreground">Date</label>
        <input type="date" value={date} max={todayIso} onChange={(e) => setDate(e.target.value)} className={INPUT_CLS} />
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-5">
            <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 dark:bg-emerald-950/40">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{checkedIn.length}</p>
            <p className="mt-0.5 text-xs font-medium text-muted-foreground">Checked in</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 dark:bg-amber-950/40">
              <Clock className="h-4 w-4 text-amber-500" />
            </div>
            <p className="text-3xl font-bold text-amber-500">{notCheckedIn.length}</p>
            <p className="mt-0.5 text-xs font-medium text-muted-foreground">Not yet</p>
          </CardContent>
        </Card>
      </div>

      <Card className="divide-y divide-border overflow-hidden p-0">
        {activeEngineers.map((eng) => {
          const done = !missingSet.has(eng.id);
          const initials = eng.name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();
          return (
            <div key={eng.id} className="flex items-center gap-3 px-4 py-3">
              <div className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                done ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400' : 'bg-muted text-muted-foreground',
              )}>
                {initials}
              </div>
              <span className="flex-1 text-sm font-medium text-foreground">{eng.name}</span>
              <Badge label={done ? 'Checked in' : 'Pending'} variant={done ? 'green' : 'yellow'} />
            </div>
          );
        })}
      </Card>
    </div>
  );
}
